import { ColumnDef } from "@tanstack/react-table";
import React, { useMemo } from "react";

import { Badge } from "../../../../components/badge/Badge";
import { SettingsIconButton } from "../../../../components/button/SettingsIconButton";
import { SettingsDropdown } from "../../../../components/dropdown/SettingsDropdown";
import { DeactivateSvg } from "../../../../components/svg/DeactivateSvg";
import { DotsSvg } from "../../../../components/svg/DotsSvg";
import { EditPencilSvg } from "../../../../components/svg/EditPencilSvg";
import { TrashSvg } from "../../../../components/svg/TrashSvg";
import { Table } from "../../../../components/table/Table";
import { compareAlphabeticallyWithNumbersFirst } from "../../../../utils/sort";
import { EmailTemplate } from "./emailTemplates.types";

type Props = {
  templates: EmailTemplate[];
  onEdit: (template: EmailTemplate) => void;
  onToggleActive: (template: EmailTemplate) => void;
  onDelete: (template: EmailTemplate) => void;
};

const stripHtml = (value: string) => value.replace(/<[^>]*>/g, " ").trim();

export const EmailTemplatesTable: React.FC<Props> = ({
  templates,
  onEdit,
  onToggleActive,
  onDelete,
}) => {
  const columns = useMemo<ColumnDef<EmailTemplate>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Template",
        sortingFn: (rowA, rowB, columnId) =>
          compareAlphabeticallyWithNumbersFirst(
            rowA.getValue(columnId),
            rowB.getValue(columnId),
          ),
        meta: {
          className: "text-[#1D2C3B] font-semibold",
        },
      },
      {
        accessorKey: "subject",
        header: "Subject",
      },
      {
        id: "body",
        header: "Message",
        accessorFn: (row) => stripHtml(row.body),
        cell: ({ getValue }) => (
          <span className={"line-clamp-1 text-gray-600"}>
            {getValue<string>() || "-"}
          </span>
        ),
        meta: {
          className: "max-w-[360px]",
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <Badge
            label={row.original.isActive ? "Active" : "Inactive"}
            variant={row.original.isActive ? "status-green" : "status-grey"}
          />
        ),
      },
      {
        accessorKey: "updatedAt",
        header: "Date Updated",
        cell: ({ getValue }) => {
          const raw = getValue() as string;
          const date = new Date(raw);

          const formatted = date.toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour12: true,
          });

          return <span className={"text-gray-600"}>{formatted}</span>;
        },
        sortingFn: (a, b, colId) =>
          new Date(a.getValue(colId)).getTime() -
          new Date(b.getValue(colId)).getTime(),
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <SettingsDropdown
            actionsList={[
              {
                name: "Edit",
                action: () => onEdit(row.original),
                icon: <EditPencilSvg />,
              },
              {
                name: row.original.isActive ? "Deactivate" : "Activate",
                action: () => onToggleActive(row.original),
                icon: <DeactivateSvg />,
              },
              {
                name: "Delete",
                action: () => onDelete(row.original),
                icon: <TrashSvg />,
                extraClasses: "text-[#DC2626]",
                iconColor: "text-[#DC2626]",
              },
            ]}>
            <SettingsIconButton icon={<DotsSvg />} onClick={() => null} />
          </SettingsDropdown>
        ),
      },
    ],
    [onDelete, onEdit, onToggleActive],
  );

  return (
    <Table
      data={templates}
      columns={columns}
      initialSorting={[{ id: "name", desc: false }]}
    />
  );
};
